import type { InsuredDocument, PolicyDocument } from "@shared/schema";
import { findAcordForm } from "@shared/acord-forms";
import type {
  COIIssuanceWithUser,
  NoteRow,
  PolicyChangeHistoryEntry,
  PolicySyncEvent,
} from "@/lib/api";
import type { PolicyRecordUpdateRow } from "@shared/recordUpdate";
import { buildRecordUpdateCard } from "@/lib/record-update-card";
import {
  additionalInsuredStatusLine,
  buildCoiSentDetailLines,
  CARRIER_SYNC_ACTOR,
  formatActivityAction,
  isCarrierSyncedStatusChange,
  isHiddenActivityLog,
} from "@/lib/activity-log-format";
import { formatTimelineTimestamp } from "@/components/insured/documents/document-helpers";
import { insuredFieldLabel } from "@/lib/insured-field-labels";
import {
  buildPolicyChangeGroups,
  localDayKey,
  syncEventTitle,
  syncEventDetail,
  systemSyncActor,
  isSuccessfulSyncOutcome,
  type PolicyContext,
} from "@/lib/policy-activity-formatter";
import {
  AOR_DETECTED_DESCRIPTION,
  AOR_DETECTED_TITLE,
  aorLogTitle,
} from "@/components/insured/policies/aor";

export type TimelineKind = "notes" | "comms" | "policy" | "insured" | "docs";

// CSR-771 — extra metadata for note events. Carried only on rows where
// `kind === "notes"`. The renderer reads this to render badges, the
// `(edited)` indicator, the `Show more` toggle, and the AMS-only Edit/Delete
// action icons.
export interface TimelineEventNoteMeta {
  noteId: string;
  source: "ams" | "nowcerts";
  insuredId: string;
  policyId: string | null;
  policyNumber: string | null;
  isEdited: boolean;
  details: string | null;
  /** CSR-1885 — global pin state + pin time (ISO). */
  pinned: boolean;
  pinnedAt: string | null;
  /** Original NoteRow — used by the Edit/Delete modals. */
  raw: NoteRow;
}

export interface TimelineEvent {
  id: string;
  kind: TimelineKind;
  timestamp: string; // ISO string
  title: string;
  body?: string;
  authorName?: string;
  sourceLabel?: string; // e.g., "Policy #POL-001" or "sync"
  // CSR-1472 — per-field change lines for insured profile edits, e.g.
  // "Company Name changed from MGMS to MGMS LLC". Carried only on
  // `kind === "insured"` events; the renderer shows them as an expandable
  // list mirroring the policy "Coverages updated" pattern.
  changes?: string[];
  // CSR-773 / R-001: when set, the renderer uses this string verbatim
  // instead of running `formatDateTime(timestamp)`. Lets a producer pin a
  // specific format (e.g. the spec-mandated `MMM D, h:mm A` for AMS-native
  // document events) without changing the legacy card or the existing
  // events that rely on `formatDateTime`.
  formattedTimestamp?: string;
  // CSR-1843 — policy change-history cards carry both spec tags: the
  // `Policy <number>` tag and the source tag (`Manual update` / `Synced from
  // <Carrier>` / `AMS System`). Distinct from `sourceLabel` (docs) so the
  // policy card can render the policy number AND the source tag together.
  policyNumber?: string;
  sourceTag?: string;
  // CSR-1960 — when set, the `Policy <number>` tag is a clickable link (record-
  // update cards set it; change-history cards leave it undefined).
  policyLink?: { insuredId: string; policyId: string };
  noteMeta?: TimelineEventNoteMeta;
  // CSR-1940 — FILTER-ONLY metadata. Never rendered by TimelineEvent.tsx; read
  // solely by applyTimelineFilters for the By-policy and Sync filter axes.
  /** Local policy id (policies[i].id, e.g. "dc-6257"); null/undefined = insured-level. */
  filterPolicyId?: string | null;
  /** True for Insured-Sync / Policy-Sync events (Sync chip predicate). */
  isSyncEvent?: boolean;
}

export interface TimelineDayGroup {
  dayKey: string; // YYYY-MM-DD
  dayLabel: string; // e.g., "TUESDAY, DECEMBER 3, 2024"
  events: TimelineEvent[];
}

export const TIMELINE_FILTER_KINDS: readonly TimelineKind[] = [
  "notes",
  "comms",
  "policy",
  "insured",
  "docs",
];

// ---------------- converters ----------------

// CSR-771 — Note rows are now first-class title/details columns (no content
// encoding). The legacy `**Title**\n\nDetails` decoder has been removed.
export function notesToEvents(notes: NoteRow[]): TimelineEvent[] {
  return notes.map((n) => ({
    id: `note-${n.id}`,
    kind: "notes",
    timestamp: (n.createdAt as unknown as string) ?? new Date().toISOString(),
    title: n.title,
    body: n.details ?? undefined,
    authorName: n.authorDisplayNameResolved,
    filterPolicyId: n.policyId ?? null, // CSR-1940
    noteMeta: {
      noteId: n.id,
      source: n.source,
      insuredId: n.insuredId,
      policyId: n.policyId,
      policyNumber: n.policyNumber,
      isEdited: n.isEdited,
      details: n.details,
      pinned: n.pinnedAt != null,
      pinnedAt: (n.pinnedAt as unknown as string) ?? null,
      raw: n,
    },
  }));
}

export function docsToEvents(
  docs: Array<PolicyDocument & { policyNumber?: string }>
): TimelineEvent[] {
  return docs
    // CSR-1908 — the auto-generated COI PDF is persisted as a
    // `policy_documents` row tagged `documentType: "certificate_of_insurance"`
    // (the 3 server auto-generate sites). Per the Event Logging Spec ("Full COI
    // history with PDFs remains in the COIs tab — timeline shows the activity
    // entry only") it must NOT surface as its own "Auto-generated Certificate of
    // Insurance" Docs card on the insured timeline — the "COI sent" card
    // (coiIssuancesToEvents) is the single timeline entry for a send. Manual
    // uploads live in `insured_documents` (no documentType) so this targets
    // only the auto-generated COIs.
    .filter((d) => d.documentType !== "certificate_of_insurance")
    .map((d) => ({
    id: `doc-${d.id}`,
    kind: "docs",
    timestamp: (d.createdAt as unknown as string) ?? new Date().toISOString(),
    title: d.name,
    body: d.notes ?? undefined,
    sourceLabel: d.policyNumber ? `Policy ${d.policyNumber}` : undefined,
    filterPolicyId: d.policyId, // CSR-1940 (schema NOT NULL)
  }));
}

// CSR-2064 — the ONE place the "ACORD form generated" card's wording lives.
// Two surfaces render this event from two different sources (the insured
// timeline from the document row, the Policy Activities tab from the activity
// log), so the words have to come from a single builder or they will drift —
// the same reason `buildCoiSentLinesFromIssuance` above is shared.

/** A policy as it appears on the card: whatever of the two facts we know. */
export interface AcordGeneratedPolicy {
  policyNumber: string | null;
  carrier: string | null;
}

/** A policy the insured holds, keyed so a stored policy id can find it. */
export interface AcordTimelinePolicy extends AcordGeneratedPolicy {
  id: string;
}

/** The insured-document columns the timeline card reads (CSR-2056). */
export type AcordTimelineDocument = InsuredDocument & {
  uploadedByName?: string | null;
};

/**
 * "ACORD Form Generated - [Form Type] — [ACORD Number]".
 *
 * The form name comes from the shared registry, keyed by the id stored on the
 * row — never from a string typed here, which would become a second copy of
 * nine names and drift from the one the Documents table shows. `savedName` is
 * the name persisted at save time, used only when the id names nothing the
 * registry defines (a form retired since the document was generated).
 */
export function acordGeneratedTitle(
  acordFormId: string | null | undefined,
  savedName: string,
): string {
  const registered = acordFormId ? findAcordForm(acordFormId) : undefined;
  return `ACORD Form Generated - ${registered?.documentName ?? savedName}`;
}

/**
 * The detail lines: one "Policy: [Number] — [Carrier]" per policy we can name,
 * then a count of any we cannot.
 *
 * `unnamedPolicyCount` covers two cases that must not be silently dropped: a
 * policy id that no longer resolves (the insured stopped carrying it), and —
 * on the Policy Activities tab — the other policies the same form covered,
 * which that tab has no way to name without fetching the insured's whole
 * policy list.
 *
 * No policies at all is the insured-only form (W-9, RPS Cargo App).
 */
export function acordGeneratedLines(
  policies: AcordGeneratedPolicy[],
  unnamedPolicyCount: number = 0,
): string[] {
  const named = policies
    .map((p) => [p.policyNumber, p.carrier].filter(Boolean).join(" — "))
    .filter((label) => label.length > 0)
    .map((label) => `Policy: ${label}`);

  if (named.length === 0 && unnamedPolicyCount === 0) {
    return ["Insured data only"];
  }
  if (unnamedPolicyCount > 0) {
    named.push(
      `and ${unnamedPolicyCount} other ${
        unnamedPolicyCount === 1 ? "policy" : "policies"
      }`,
    );
  }
  return named;
}

// CSR-773: AMS-native insured-document events. The list source MUST include
// soft-deleted rows so the timeline keeps showing the event after a user
// deletes the underlying document (AC-6) — CSR-2064 relies on the same
// property for generated ACORD forms.
// Source label is intentionally omitted — AMS-native events render with no
// source badge per spec.
// R-001: pre-format the timestamp via `formatTimelineTimestamp` and write
// it into `formattedTimestamp` so the renderer skips the legacy
// `formatDateTime` and emits the spec-mandated `MMM D, h:mm A` shape.
export function insuredDocsToEvents(
  docs: AcordTimelineDocument[],
  // CSR-2064 — the insured's policies, so a generated form's stored policy ids
  // can be shown as numbers and carriers. Optional: uploads need none.
  policies: AcordTimelinePolicy[] = [],
): TimelineEvent[] {
  const policyById = new Map(policies.map((p) => [p.id, p]));
  return docs.map((d) => {
    const fileSize = d.fileSize ?? 0;
    const sizeStr =
      fileSize < 1024
        ? `${fileSize} B`
        : fileSize < 1024 * 1024
        ? `${(fileSize / 1024).toFixed(1)} KB`
        : `${(fileSize / 1024 / 1024).toFixed(1)} MB`;
    const isoTimestamp =
      (d.createdAt as unknown as string) ?? new Date().toISOString();
    const common = {
      id: `idoc-${d.id}`,
      kind: "docs" as const,
      timestamp: isoTimestamp,
      formattedTimestamp: formatTimelineTimestamp(isoTimestamp),
      authorName: d.uploadedByName ?? "Unknown",
      filterPolicyId: null, // CSR-1940 — insured-level document
      // No sourceLabel — TimelineEventCard renders the badge only when set.
    };

    // CSR-2064 — a generated ACORD form is a different action from an upload
    // and says so. The policy lines carry the detail; the file name and size
    // are dropped because for a generated form they only restate the title.
    if (d.source === "acord_form") {
      const storedPolicyIds = d.policyIds ?? [];
      const namedPolicies = storedPolicyIds
        .map((id) => policyById.get(id))
        .filter((p): p is AcordTimelinePolicy => p !== undefined);
      return {
        ...common,
        title: acordGeneratedTitle(d.acordFormId, d.name),
        changes: acordGeneratedLines(
          namedPolicies,
          storedPolicyIds.length - namedPolicies.length,
        ),
      };
    }

    return {
      ...common,
      title: `Document Uploaded - ${d.name}`,
      body: `${d.fileName} • ${sizeStr}`,
    };
  });
}

// CSR-1908 — a COI send surfaces exactly ONE "COI sent" Docs card on the insured
// timeline, sourced from the insured COI issuance history (NOT the activity-log
// feed, whose `coi.sent`/`coi_request.sent` rows live on entity types the feed
// never selects). Reuses the CSR-1878 title map (`formatActivityAction`) and the
// exact CSR-1878 detail-line builder (`buildCoiSentDetailLines`) so the card
// presents identically to the global Activity Log. Only completed sends
// (`deliveryStatus === "sent"`) are shown — `processing`/`failed` are excluded.
// The policy number is intentionally omitted (not a returned issuance column;
// it lives only inside nested jsonb) — the builder degrades gracefully.
// CSR-1746 — the issuance→"COI sent" detail-line mapping, extracted so the
// insured timeline (coiIssuancesToEvents) and the Policy Activity tab's
// consolidated COI card render identically. Wraps the shared CSR-1878
// `buildCoiSentDetailLines` base builder with the issuance field mapping +
// recipient precedence (redirected → recipient email → recipient name).
export function buildCoiSentLinesFromIssuance(
  i: Pick<
    COIIssuanceWithUser,
    | "certificateHolderName"
    | "redirectedRecipientEmail"
    | "recipientEmail"
    | "recipientName"
    | "insuredName"
  >,
): string[] {
  return buildCoiSentDetailLines({
    certificateHolderName: i.certificateHolderName,
    recipient:
      i.redirectedRecipientEmail ??
      i.recipientEmail ??
      i.recipientName ??
      undefined,
    insuredName: i.insuredName ?? undefined,
  });
}

export function coiIssuancesToEvents(
  issuances: COIIssuanceWithUser[],
): TimelineEvent[] {
  return issuances
    .filter((i) => i.deliveryStatus === "sent")
    .map((i) => {
      const isoTimestamp =
        ((i.sentAt ?? i.issuedAt) as unknown as string) ??
        new Date().toISOString();
      const changes = buildCoiSentLinesFromIssuance(i);
      return {
        id: `coi-${i.id}`,
        kind: "docs" as const,
        timestamp: isoTimestamp,
        formattedTimestamp: formatTimelineTimestamp(isoTimestamp),
        title: formatActivityAction("coi.sent"),
        changes: changes.length ? changes : undefined,
        authorName: i.issuedByName ?? undefined,
      };
    });
}

export interface ActivityLogLike {
  id: string;
  entityType: string;
  entityId: string;
  action: string;
  entityName: string | null;
  details: string | null;
  createdAt: string | Date;
  userName?: string | null;
  userEmail?: string | null;
}

// CSR-975 — the sync writes a System-actor `status_updated` policy log when a
// carrier flips a policy into a cancellation state. The untrusted carrier
// banner message rides in `details.reason`. Parse defensively (`details` is a
// nullable text column) and special-case the title/body so the generic path
// doesn't render "policy status_updated".
interface StatusUpdatedDetails {
  fromStatus?: string | null;
  toStatus?: string | null;
  reason?: string | null;
  effectiveDate?: string | null;
}

function parseDetails(raw: string | null): Record<string, unknown> | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

// CSR-1293 — a per-policy sync-outcome audit row written by the policy sync
// poll worker / `checkAndCompleteSyncJob`. Tagged `action: "synced"` on a
// `policy` entity with `details: { singlePolicy: true, carrierSync: true, … }`.
// These exist for the per-policy sync-status reader and must NOT surface on the
// insured Overview timeline — a single manual refresh fans out one per policy.
function isPolicySyncAuditLog(l: ActivityLogLike): boolean {
  if (l.entityType !== "policy" || l.action !== "synced") return false;
  const details = parseDetails(l.details);
  return details?.singlePolicy === true && details?.carrierSync === true;
}

// CSR-1293 (R-001) — a manual insured refresh ALSO writes a CSR-1117
// best-effort "saved COI template cleared" audit row (`entityType: "insured",
// action: "updated"` tagged `details: { clearedCoiTemplate: true }`) via
// `clearSavedCoiTemplateForInsured`. It shares the refresh timestamp with the
// `manual_sync` event, so without this guard a single refresh renders TWO
// insured-level events ("Insured Sync…" + a generic "insured updated"). Suppress
// it from the timeline (presentation-only — the DB row is intentionally left
// intact, same as the per-policy `synced` rows). This is narrowly scoped to the
// `clearedCoiTemplate` tag so genuine manual insured edits (`insured`/`updated`
// without the tag) still render.
function isCoiTemplateClearAuditLog(l: ActivityLogLike): boolean {
  if (l.entityType !== "insured" || l.action !== "updated") return false;
  return parseDetails(l.details)?.clearedCoiTemplate === true;
}

// CSR-1472 — the per-field diff the PATCH /api/insureds/:id route writes into
// the activity-log `details.changes` array (one entry per genuinely-changed
// tracked field). `oldValue`/`newValue` are the persisted strings (or null).
interface InsuredEditChange {
  field: string;
  oldValue: string | null;
  newValue: string | null;
}

const EMPTY_VALUE_DISPLAY = "(empty)";

function formatInsuredEditValue(value: string | null | undefined): string {
  if (value == null || String(value).trim() === "") return EMPTY_VALUE_DISPLAY;
  return String(value);
}

// CSR-1472 — turn an `insured`/`updated` activity log into a single grouped
// Insured event. New rows carry `details.changes` (full before/after) which
// drives the field list + the count-based title; legacy rows carry only
// `details.changed` (field keys) so they still categorize as Insured but show
// no before/after detail (it can't be reconstructed).
function insuredEditEvent(
  log: ActivityLogLike,
  timestamp: string,
  authorName: string,
): TimelineEvent | null {
  const details = parseDetails(log.details);
  // A present `changes` array (even empty) is the authoritative diff written by
  // the PATCH route; only legacy rows that predate it fall back to the bare
  // `changed` field keys.
  const hasStructuredDiff = Array.isArray(details?.changes);
  const rawChanges = hasStructuredDiff
    ? (details!.changes as InsuredEditChange[])
    : [];
  // CSR-1746 — drop no-op field diffs (a value that formats identically on both
  // sides, e.g. a whitespace/format-only delta) so they never render as
  // "X changed from V to V" and never inflate the field count.
  const survivingRaw = rawChanges.filter(
    (c) =>
      formatInsuredEditValue(c.oldValue) !==
      formatInsuredEditValue(c.newValue),
  );

  // A structured edit whose every field diff is a no-op is a phantom card —
  // drop it entirely. Legacy rows (no `changes` array, only `changed` keys)
  // are unaffected and still render.
  if (hasStructuredDiff && survivingRaw.length === 0) return null;

  const changes = survivingRaw.map(
    (c) =>
      `${insuredFieldLabel(c.field)} changed from ${formatInsuredEditValue(
        c.oldValue,
      )} to ${formatInsuredEditValue(c.newValue)}`,
  );

  const changedFields = hasStructuredDiff
    ? survivingRaw.map((c) => c.field)
    : Array.isArray(details?.changed)
    ? (details!.changed as string[])
    : [];
  const labels = Array.from(new Set(changedFields.map(insuredFieldLabel)));
  const title =
    labels.length === 1
      ? `${labels[0]} updated`
      : labels.length > 1
      ? `${labels.length} fields updated`
      : "Insured updated";

  return {
    id: `log-${log.id}`,
    kind: "insured" as const,
    timestamp,
    title,
    changes: changes.length ? changes : undefined,
    authorName,
    filterPolicyId: null, // CSR-1940 — insured-level edit
  };
}

// CSR-1637 — manual changes to the insured's child entities (Additional Insured,
// Loss Payee, Waiver of Subrogation, Commodity) are logged keyed to the child row
// and scoped back via `details.insuredId` (server side). They render as
// insured-kind events titled e.g. "Additional Insured added — Fox Carriers".
export const CHILD_ENTITY_LABEL: Record<string, string> = {
  additional_insured: "Additional Insured",
  loss_payee: "Loss Payee",
  waiver_of_subrogation: "Waiver of Subrogation",
  commodity: "Commodity",
};

const CHILD_ACTION_VERB: Record<string, string> = {
  created: "added",
  updated: "updated",
  deleted: "removed",
};

export function isChildEntityLog(log: ActivityLogLike): boolean {
  return (
    log.entityType in CHILD_ENTITY_LABEL && log.action in CHILD_ACTION_VERB
  );
}

export function childEntityTitle(entityType: string, action: string, name: string | null): string {
  const label = CHILD_ENTITY_LABEL[entityType] ?? entityType;
  const verb = CHILD_ACTION_VERB[action] ?? action;
  const trimmed = name?.trim();
  return trimmed ? `${label} ${verb} — ${trimmed}` : `${label} ${verb}`;
}

function childEntityEvent(
  log: ActivityLogLike,
  timestamp: string,
  authorName: string,
): TimelineEvent {
  const statusLine = additionalInsuredStatusLine(log);
  return {
    id: `log-${log.id}`,
    kind: "insured" as const,
    timestamp,
    title: childEntityTitle(log.entityType, log.action, log.entityName),
    changes: statusLine ? [statusLine] : undefined,
    authorName: isCarrierSyncedStatusChange(log) ? CARRIER_SYNC_ACTOR : authorName,
    filterPolicyId: null, // CSR-1940 — insured-level child-entity change
  };
}

function statusUpdatedBody(details: StatusUpdatedDetails | null): string | undefined {
  const reason = details?.reason;
  if (typeof reason === "string" && reason.trim()) return reason;
  if (details?.effectiveDate) {
    return `This policy will be canceled on ${details.effectiveDate}.`;
  }
  if (details?.toStatus) {
    return `Policy status changed to ${details.toStatus}.`;
  }
  return undefined;
}

export function activityLogsToEvents(
  logs: ActivityLogLike[],
  insuredId: string,
  policyIds: Set<string>
): TimelineEvent[] {
  return logs
    .filter((l) => {
      if (l.entityType === "insured" && l.entityId === insuredId) return true;
      if (l.entityType === "policy" && policyIds.has(l.entityId)) {
        // CSR-1843 — a manual policy edit writes BOTH a rich change-history card
        // AND this generic activity-log `updated` twin. Suppress the twin ONLY
        // when a change-history counterpart exists, so a single edit renders
        // once WITHOUT hiding edits that produced no card. The server marks the
        // two card-producing writers deterministically (server/routes.ts):
        //   • main PATCH → `details.changed` (empty on a no-op resubmit or a
        //     failed asset-flip with no diff → keep the generic card, it's the
        //     only trace of the edit);
        //   • coverages PUT → `details.coverageKeys` (a coverage edit always
        //     writes a `coverages` change-history row).
        // Either marker being non-empty is proof the rich card exists. CSR-1844
        // additionally adds `changed:["coverages"]` to the coverages write —
        // harmless, either marker independently triggers suppression.
        if (l.action !== "updated") return true;
        const details = parseDetails(l.details);
        const changed = Array.isArray(details?.changed) ? details.changed : [];
        const coverageKeys = Array.isArray(details?.coverageKeys)
          ? details.coverageKeys
          : [];
        return changed.length === 0 && coverageKeys.length === 0;
      }
      // CSR-1637 — child-entity logs are already scoped to this insured by the
      // server feed (`details.insuredId`); render them regardless of entityId
      // (which is the child row's id, not the insured's).
      if (isChildEntityLog(l)) return true;
      return false;
    })
    // CSR-1293 — drop the per-policy `synced` audit rows the sync poll worker
    // writes (tagged `{ singlePolicy, carrierSync }` in `details`). They power
    // the per-policy sync-status reader (`getLatestPolicySyncOutcomes`), not
    // the user-facing timeline: one manual refresh fans out to one such row per
    // policy, which previously rendered as several redundant "policy synced"
    // entries. The single user-facing event is the `insured`/`manual_sync` row
    // written synchronously by `POST /api/insureds/:id/sync` (rendered below).
    // R-001: also drop the CSR-1117 `clearedCoiTemplate` audit row a manual
    // sync emits, so the refresh renders exactly ONE insured-level event.
    .filter((l) => !isPolicySyncAuditLog(l) && !isCoiTemplateClearAuditLog(l))
    // CSR-1908 — drop internal/engineering rows that would otherwise fall through
    // to the generic `"${entityType} ${action}"` card (policy `emailed`,
    // `email_queued`, `csr_1003_orphan_debounce_soft_delete`,
    // `orphan_debounce_restore`, and any future internal sibling the shared
    // predicate catches). Reuse the global-Activity-Log hide predicate rather
    // than re-listing actions here. Exempt the two action-classes this timeline
    // intentionally OWNS via dedicated branches below: `manual_sync` ("Insured
    // Sync — Manually triggered", CSR-1293) and every `updated` row (CSR-1843
    // policy twin-suppression + CSR-1472 insured edits already govern these).
    // `isHiddenActivityLog` lists `manual_sync`/`sync` and treats a no-op
    // `updated` as hidden, so without the exemption it would wrongly drop those
    // established events. No leaking internal action is ever `updated`/
    // `manual_sync`, so this removes only the leak.
    .filter((l) => {
      if (l.action === "updated" || l.action === "manual_sync") return true;
      // CSR-1940 (D1=B, R4) — a TIMELINE-LOCAL exemption for exactly the two
      // COI-triggered sync outcomes so they surface as Policy-Sync cards below.
      // This does NOT touch the shared `isHiddenActivityLog` predicate: every
      // other "sync"/internal action it catches stays dropped (CSR-1908).
      if (
        l.action === "sync_failed_for_coi" ||
        l.action === "sync_skipped_for_coi"
      ) {
        return true;
      }
      return !isHiddenActivityLog(l);
    })
    .map((l): TimelineEvent | null => {
      const authorName = l.userName ?? l.userEmail ?? "System";
      const timestamp =
        (l.createdAt as unknown as string) ?? new Date().toISOString();
      // CSR-1293 — the one user-facing event for a manual insured refresh.
      // Authored by the user who clicked Refresh (the route stamps `req.user`).
      // CSR-2169 — when the fan-out skipped ineligible policies (older terms /
      // unsupported carrier), the route records a summary; surface it so the
      // refresh never silently syncs fewer policies than the insured has.
      if (l.entityType === "insured" && l.action === "manual_sync") {
        const syncSummary = parseDetails(l.details)?.syncSummary;
        return {
          id: `log-${l.id}`,
          kind: "policy" as const,
          timestamp,
          title: "Insured Sync — Manually triggered",
          ...(typeof syncSummary === "string" && syncSummary.trim()
            ? { body: syncSummary }
            : {}),
          authorName,
          isSyncEvent: true, // CSR-1940
          filterPolicyId: null, // insured-level sync
        };
      }
      // CSR-1472 — manual insured profile edits become a dedicated Insured
      // event with field-level before→after detail (the cleared-COI-template
      // audit row is already dropped by the filter above).
      if (l.entityType === "insured" && l.action === "updated") {
        return insuredEditEvent(l, timestamp, authorName);
      }
      // CSR-1637 — Additional Insured / Loss Payee / Waiver manual changes.
      if (isChildEntityLog(l)) {
        return childEntityEvent(l, timestamp, authorName);
      }
      // CSR-1940 (D1=B) — COI-triggered sync failures/skips surface as
      // Policy-Sync cards. The outcome is in the title (matching the CSR-1746
      // outcome-in-title convention); the COI trigger rides in the actor.
      if (
        l.entityType === "policy" &&
        (l.action === "sync_failed_for_coi" || l.action === "sync_skipped_for_coi")
      ) {
        const details = parseDetails(l.details);
        const errorMessage =
          typeof details?.errorMessage === "string" && details.errorMessage.trim()
            ? details.errorMessage
            : null;
        const reason =
          typeof details?.reason === "string" && details.reason.trim()
            ? details.reason
            : null;
        const failed = l.action === "sync_failed_for_coi";
        return {
          id: `log-${l.id}`,
          kind: "policy" as const,
          timestamp,
          title: failed ? "Policy Sync — Failed" : "Policy Sync — Skipped",
          body:
            errorMessage ??
            reason ??
            (failed
              ? "The COI-triggered sync did not complete."
              : "The COI-triggered sync was skipped."),
          authorName: "COI request",
          isSyncEvent: true,
          filterPolicyId: l.entityId,
        };
      }
      // CSR-2327 — an agent's AOR flag / "Access restored" on one of the
      // insured's policies.
      const aorTitle = aorLogTitle(l);
      if (aorTitle) {
        return {
          id: `log-${l.id}`,
          kind: "policy" as const,
          timestamp,
          title: aorTitle,
          body: l.entityName ? `Policy ${l.entityName}` : undefined,
          authorName,
          filterPolicyId: l.entityId,
        };
      }
      if (l.entityType === "policy" && l.action === "status_updated") {
        return {
          id: `log-${l.id}`,
          kind: "policy" as const,
          timestamp,
          title: "Policy Status updated",
          body: statusUpdatedBody(parseDetails(l.details) as StatusUpdatedDetails | null),
          authorName,
          filterPolicyId: l.entityId, // CSR-1940 — D2=No: not tagged isSyncEvent
        };
      }
      return {
        id: `log-${l.id}`,
        kind: "policy" as const,
        timestamp,
        title: `${l.entityType} ${l.action}`,
        body: l.entityName ?? undefined,
        authorName,
        // CSR-1940 — a policy entity carries its id; insured/other entities are
        // insured-level (their entityId is not a policy id).
        filterPolicyId: l.entityType === "policy" ? l.entityId : null,
      };
    })
    // CSR-1746 — `insuredEditEvent` returns null for a fully-phantom structured
    // insured edit (every field diff is a no-op); drop it. Only the
    // `insured`/`updated` branch can return null.
    .filter((e): e is TimelineEvent => e !== null);
}

// CSR-1843 — per-policy change-history rows become Policy-kind timeline cards
// via the shared `buildPolicyChangeGroups`, so the Insured Overview timeline
// renders the identical content the Policy Activity tab shows. Each card carries
// the `Policy <number>` tag and the derived source tag (Manual/Synced/AMS System).
export function policyChangeHistoryToEvents(
  changeHistory: PolicyChangeHistoryEntry[],
  policy: PolicyContext,
  policyNumber: string | null,
  // CSR-1940 — the local policy id for the By-policy filter axis. Distinct from
  // `policyNumber` (a display string with rendering side-effects); the filter
  // must key on the structured id, never the rendered tag.
  policyId?: string | null,
): TimelineEvent[] {
  return buildPolicyChangeGroups(changeHistory, policy).map((g) => ({
    id: `pch-${g.id}`,
    kind: "policy",
    timestamp: g.changedAt,
    title: g.title,
    changes: g.changeLines.length ? g.changeLines : undefined,
    body: g.note ?? undefined,
    authorName: g.actor ?? undefined,
    sourceTag: g.sourceTag,
    policyNumber: policyNumber ?? undefined,
    filterPolicyId: policyId ?? null, // CSR-1940
    isSyncEvent: g.source === "sync", // CSR-1940 — carrier-scrape change cards
  }));
}

// CSR-2332 — one "AOR detected" card per automatic detection, matching the
// Policy Activity tab; it is a carrier-sync outcome, so it answers the Sync chip.
export function aorDetectedToEvents(
  detectedAt: Date[],
  policyNumber: string | null,
  policyId: string,
): TimelineEvent[] {
  return detectedAt.map((occurredAt, i) => ({
    id: `aor-detected-${policyId}-${i}`,
    kind: "policy",
    timestamp: occurredAt.toISOString(),
    title: AOR_DETECTED_TITLE,
    body: AOR_DETECTED_DESCRIPTION,
    authorName: "System",
    policyNumber: policyNumber ?? undefined,
    filterPolicyId: policyId,
    isSyncEvent: true,
  }));
}

// CSR-1955 / CSR-1960 — record-update cards for the insured timeline (shared
// buildRecordUpdateCard). The Policy tag is clickable here (policyLink).
export function policyRecordUpdatesToEvents(
  rows: PolicyRecordUpdateRow[],
  insuredId: string,
  policyNumber: string | null,
  policyId: string,
): TimelineEvent[] {
  return rows.map((row) => {
    const card = buildRecordUpdateCard(row);
    return {
      id: `ru-${row.id}`,
      kind: "policy" as const,
      timestamp: row.createdAt,
      title: card.title,
      changes: card.changeLines.length ? card.changeLines : undefined,
      authorName: card.authorName,
      policyNumber: policyNumber ?? undefined,
      policyLink: policyId ? { insuredId, policyId } : undefined,
      filterPolicyId: policyId, // CSR-1940 By-policy filter axis
    };
  });
}

// CSR-1940 (D1=B) — surface carrier-sync FAILURES (and policy REMOVALS) on the
// insured timeline via a per-policy fan-out of the existing `/sync-events` feed.
// Only non-success outcomes emit a run card, PLUS a removed-policy success
// (`outcome:"succeeded", removed:true`) — a genuine, noteworthy event the Policy
// Activity tab already shows. Ordinary successful carrier syncs stay represented
// by their field-change cards (policyChangeHistoryToEvents); neither a failure
// nor a removal produces a change card, so this can never collide with source 2.
// Renders sync runs with the SAME extracted helpers the Policy Activity tab uses.
//
// CSR-1977 — `no_change` is a success and is filtered out here for the same
// reason `succeeded` is: it is an ordinary carrier sync, so emitting a run card
// would put a "nothing happened" entry on the insured timeline for every
// routine re-check of every policy. The per-policy Activity tab still shows it,
// which is where "did that sync actually do anything?" gets answered.
export function policySyncEventsToEvents(
  events: PolicySyncEvent[],
  policy: PolicyContext,
  policyId: string,
): TimelineEvent[] {
  return events
    .filter((e) => !isSuccessfulSyncOutcome(e.outcome) || e.removed)
    .map((e) => ({
      id: `psync-${e.id}`,
      kind: "policy" as const,
      timestamp: e.occurredAt,
      title: syncEventTitle(e), // "Policy Sync — Failed"
      body: syncEventDetail(e), // failure reason / removed note
      authorName: e.performedBy ?? systemSyncActor(policy),
      isSyncEvent: true,
      filterPolicyId: policyId,
    }));
}

// ---------------- merging + grouping ----------------

export function sortByTimestampDesc(events: TimelineEvent[]): TimelineEvent[] {
  return [...events].sort((a, b) => b.timestamp.localeCompare(a.timestamp));
}

const DAY_LABEL_FMT = new Intl.DateTimeFormat(undefined, {
  weekday: "long",
  month: "long",
  day: "numeric",
  year: "numeric",
});

export function groupByDay(events: TimelineEvent[]): TimelineDayGroup[] {
  const groups = new Map<string, TimelineEvent[]>();
  for (const e of events) {
    const d = new Date(e.timestamp);
    const dayKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    if (!groups.has(dayKey)) groups.set(dayKey, []);
    groups.get(dayKey)!.push(e);
  }
  return Array.from(groups.entries())
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([dayKey, evs]) => ({
      dayKey,
      dayLabel: DAY_LABEL_FMT.format(new Date(dayKey + "T00:00:00")).toUpperCase(),
      events: evs,
    }));
}

export function filterByKinds(
  events: TimelineEvent[],
  kinds: TimelineKind[] | null
): TimelineEvent[] {
  if (!kinds || kinds.length === 0) return events;
  const set = new Set(kinds);
  return events.filter((e) => set.has(e.kind));
}

export function mergeSources(sources: TimelineEvent[][]): TimelineEvent[] {
  return sortByTimestampDesc(sources.flat());
}

// ---------------- CSR-1940: navigation filters ----------------

// The chip axis is single-select. "sync" is a predicate over `isSyncEvent`, not
// a 6th TimelineKind — so it never ripples into the badge/icon/kind maps.
export type TimelineChip = "all" | "sync" | TimelineKind;

export interface TimelineFilterState {
  active: TimelineChip;
  policyId: string | "all";
  from?: Date; // inclusive, local day
  to?: Date; // inclusive, local day
}

// Reuses `localDayKey` (shared with the Policy tab) so day-range comparison and
// day-grouping stay on one implementation. Lexicographic compare is valid for
// the zero-padded "YYYY-MM-DD" keys it produces.
function eventWithinDayRange(ts: string, from?: Date, to?: Date): boolean {
  const day = localDayKey(ts);
  if (from && day < localDayKey(from)) return false;
  if (to && day > localDayKey(to)) return false;
  return true;
}

// The single filtering truth: the chip axis AND the policy axis AND the date
// range all compose. Pure over the already-merged event set.
export function applyTimelineFilters(
  events: TimelineEvent[],
  { active, policyId, from, to }: TimelineFilterState,
): TimelineEvent[] {
  let out = events;
  if (active === "sync") out = out.filter((e) => e.isSyncEvent === true);
  else if (active !== "all") out = filterByKinds(out, [active]);
  if (policyId !== "all") out = out.filter((e) => e.filterPolicyId === policyId);
  if (from || to) out = out.filter((e) => eventWithinDayRange(e.timestamp, from, to));
  return out;
}
